// The prerequisite connector shapes on /tech (Issue 21), as the game draws its tree. Pure
// geometry over two cell rectangles so the shapes are testable without a DOM; the route
// measures the cells and calls this.

export interface CellRect {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

const centerX = (rect: CellRect) => rect.left + (rect.right - rect.left) / 2;
const centerY = (rect: CellRect) => rect.top + (rect.bottom - rect.top) / 2;

/** The SVG path from a prerequisite's cell to its dependent's. */
export function routePrerequisiteLine(from: CellRect, to: CellRect): string {
  const toY = centerY(to);
  if (to.left < from.right) {
    // Same column (the cells overlap horizontally): a vertical stroke between the facing edges.
    const below = toY > centerY(from);
    return `M ${centerX(from)} ${below ? from.bottom : from.top} V ${below ? to.top : to.bottom}`;
  }
  if (toY === centerY(from)) {
    // Same row: one straight stroke, right edge to left edge.
    return `M ${from.right} ${toY} H ${to.left}`;
  }
  // Different row: leave the prerequisite's bottom centre (top centre when the dependent
  // sits above), travel to the dependent's centre line, then run into its left edge.
  const exitY = toY > centerY(from) ? from.bottom : from.top;
  return `M ${centerX(from)} ${exitY} V ${toY} H ${to.left}`;
}

/** A subject as the line pass needs it: its id and the ids it requires. */
export interface LinkedSubject {
  id: string;
  prerequisites: readonly string[];
}

/** One drawn connector: the pair it joins and the stroke between them. It carries the two ids
 * because the geometry alone does not identify it — two different pairs can produce the very
 * same `d` string (two prerequisites of one subject that leave the same edge, or two subjects
 * whose cells sit at the same coordinates in different bands), and React keyed the paths by
 * `d`, which duplicated keys and let it drop strokes. Nine such collisions on the TEC Military
 * tree alone, measured 2026-09-06. */
export interface PrerequisiteLine {
  /** The prerequisite's subject id — where the stroke starts. */
  from: string;
  /** The dependent's subject id — where it ends. */
  to: string;
  /** The SVG `d` attribute. */
  path: string;
}

/** One connector per prerequisite pair whose two cells are both laid out. Cross-domain
 * prerequisites have no cell in a tree view and draw nothing; the tooltip still names them. */
export function prerequisiteLines(
  visible: readonly LinkedSubject[],
  rects: ReadonlyMap<string, CellRect>,
): PrerequisiteLine[] {
  const lines: PrerequisiteLine[] = [];
  for (const subject of visible) {
    const to = rects.get(subject.id);
    if (!to) continue;
    for (const prereqId of subject.prerequisites) {
      const from = rects.get(prereqId);
      if (from) {
        lines.push({
          from: prereqId,
          to: subject.id,
          path: routePrerequisiteLine(from, to),
        });
      }
    }
  }
  return lines;
}
